import { Action } from '@ngrx/store';
import { PROCESS_FEATURE, StateUtils } from '@spartacus/core';
import { PagedSearchAction } from '../../../../core/features/store/actions/search.actions';
import {
  B2BUser,
  B2BUserSearchOptimizationOptions,
  B2BUserSearchParams,
  CustomerStatus,
  CustomerStatusEnum,
  CustomerStatusList,
  HttpErrorModel,
  OrgCustomerCreation,
  OrgCustomerModification,
  OrgCustomerShipToAssignment,
  OrgUnitUserPage,
  SearchParams,
  UpdateStatusOptions,
  UpdateStatusValue,
  UserRoleList,
} from '../../../../core/model';
import { PyStateUtils } from '../../../../core/state/utils';
import {
  B2B_USER_ENTITIES,
  B2B_USER_SEARCH,
  B2B_USER_STATUSES,
  B2B_USERS_DELETE,
  CREATE_B2B_USER_PROCESS,
  USER_ROLES,
} from '../organization-state';

export const CLEAR_ORGANIZATION_DATA = '[Organization] Clear Data';

export const LOAD_B2B_USER = '[B2BUser] Load B2BUser Data';
export const LOAD_B2B_USER_FAIL = '[B2BUser] Load B2BUser Data Fail';
export const LOAD_B2B_USER_SUCCESS = '[B2BUser] Load B2BUser Data Success';

export const CREATE_B2B_USER = '[B2BUser] Create B2BUser';
export const CREATE_B2B_USER_FAIL = '[B2BUser] Create B2BUser Fail';
export const CREATE_B2B_USER_SUCCESS = '[B2BUser] Create B2BUser Success';

export const UPDATE_B2B_USER = '[B2BUser] Update B2BUser Data';
export const UPDATE_B2B_USER_FAIL = '[B2BUser] Update B2BUser Data Fail';
export const UPDATE_B2B_USER_SUCCESS = '[B2BUser] Update B2BUser Data Success';

export const UPDATE_B2B_USER_ROLE = '[B2BUser] Update B2BUser Role';
export const UPDATE_B2B_USER_ROLE_FAIL = '[B2BUser] Update B2BUser Role Fail';
export const UPDATE_B2B_USER_ROLE_SUCCESS = '[B2BUser] Update B2BUser Role Success';

export const UPDATE_B2B_USER_SOLDTOS = '[B2BUser] Update B2BUser SoldTos';
export const UPDATE_B2B_USER_SOLDTOS_FAIL = '[B2BUser] Update B2BUser SoldTos Fail';
export const UPDATE_B2B_USER_SOLDTOS_SUCCESS = '[B2BUser] Update B2BUser SoldTos Success';

export const UPDATE_B2B_USER_SHIP_TO_ASSIGNMENT = '[B2BUser] Update B2BUser Ship To Assignment';
export const UPDATE_B2B_USER_SHIP_TO_ASSIGNMENT_FAIL = '[B2BUser] Update B2BUser Ship To Assignment Fail';
export const UPDATE_B2B_USER_SHIP_TO_ASSIGNMENT_SUCCESS = '[B2BUser] Update B2BUser Ship To Assignment Success';

export const DELETE_B2B_USERS = '[B2BUser] Delete B2BUser Data';
export const DELETE_B2B_USERS_FAIL = '[B2BUser] Delete B2BUser Data Fail';
export const DELETE_B2B_USERS_SUCCESS = '[B2BUser] Delete B2BUser Data Success';

export const SEARCH_B2B_USERS = '[B2BUser] Search B2BUsers';
export const SEARCH_B2B_USERS_FAIL = '[B2BUser] Search B2BUsers Fail';
export const SEARCH_B2B_USERS_SUCCESS = '[B2BUser] Search B2BUsers Success';
export const CLEAR_B2B_USERS_SEARCH = '[B2BUser] Clear B2BUsers Search';

export const LOAD_B2B_USER_STATUSES = '[B2BUser] Load B2BUser Statuses';
export const LOAD_B2B_USER_STATUSES_FAIL = '[B2BUser] Load B2BUser Statuses Fail';
export const LOAD_B2B_USER_STATUSES_SUCCESS = '[B2BUser] Load B2BUser Statuses Success';

export const LOAD_USER_ROLES = '[B2BUser] Load User Roles';
export const LOAD_USER_ROLES_FAIL = '[B2BUser] Load User Roles Fail';
export const LOAD_USER_ROLES_SUCCESS = '[B2BUser] Load User Roles Success';

export const UPDATE_B2B_USER_STATUS = '[B2BUser] Update B2BUser Status';
export const UPDATE_B2B_USER_STATUS_FAIL = '[B2BUser] Update B2BUser Status Fail';
export const UPDATE_B2B_USER_STATUS_SUCCESS = '[B2BUser] Update B2BUser Status Success';

interface SearchB2BUsersPayload {
  userId: string;
  searchParams?: B2BUserSearchParams;
  optimizationOptions?: B2BUserSearchOptimizationOptions;
}

interface SearchB2BUsersFailPayload extends SearchB2BUsersPayload {
  error?: HttpErrorModel;
}

interface SearchB2BUsersSuccessPayload extends SearchB2BUsersPayload {
  searchResultPage: OrgUnitUserPage;
}

export class OrganizationClearData implements Action {
  readonly type = CLEAR_ORGANIZATION_DATA;
}

export class LoadB2BUser extends StateUtils.EntityLoadAction {
  readonly type = LOAD_B2B_USER;
  constructor(public payload: { userId: string; orgCustomerId: string }) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId);
  }
}

export class LoadB2BUserFail extends PyStateUtils.EntityFailAction {
  readonly type = LOAD_B2B_USER_FAIL;
  constructor(public payload: { orgCustomerId: string; error: any }) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId, payload.error);
  }
}

export class LoadB2BUserSuccess extends StateUtils.EntitySuccessAction {
  readonly type = LOAD_B2B_USER_SUCCESS;
  constructor(public payload: B2BUser | B2BUser[]) {
    super(
      B2B_USER_ENTITIES,
      Array.isArray(payload) ? payload.map((orgUnitCustomer) => orgUnitCustomer?.customerId) : payload?.customerId
    );
  }
}

export class CreateB2BUser extends StateUtils.EntityLoadAction {
  readonly type = CREATE_B2B_USER;
  constructor(public payload: { userId: string; orgCustomerCreation: OrgCustomerCreation }) {
    super(PROCESS_FEATURE, CREATE_B2B_USER_PROCESS);
  }
}

export class CreateB2BUserFail extends PyStateUtils.EntityFailAction {
  readonly type = CREATE_B2B_USER_FAIL;
  constructor(public payload: any) {
    super(PROCESS_FEATURE, CREATE_B2B_USER_PROCESS, payload);
  }
}

export class CreateB2BUserSuccess extends StateUtils.EntitySuccessAction {
  readonly type = CREATE_B2B_USER_SUCCESS;
  constructor(public payload: B2BUser) {
    super(PROCESS_FEATURE, CREATE_B2B_USER_PROCESS, payload);
  }
}

export class UpdateB2BUser extends StateUtils.EntityLoadAction {
  readonly type = UPDATE_B2B_USER;
  constructor(
    public payload: {
      userId: string;
      orgCustomerId: string;
      orgCustomerModification: OrgCustomerModification;
    }
  ) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId);
  }
}

export class UpdateB2BUserFail extends PyStateUtils.EntityFailAction {
  readonly type = UPDATE_B2B_USER_FAIL;
  constructor(public payload: { orgCustomerId: string; error: HttpErrorModel }) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId, payload.error);
  }
}

export class UpdateB2BUserSuccess extends StateUtils.EntitySuccessAction {
  readonly type = UPDATE_B2B_USER_SUCCESS;
  constructor(public payload: B2BUser) {
    super(B2B_USER_ENTITIES, payload.customerId, payload);
  }
}

export class UpdateB2BUserRole extends StateUtils.EntityLoadAction {
  readonly type = UPDATE_B2B_USER_ROLE;
  constructor(
    public payload: {
      userId: string;
      orgCustomerId: string;
      roleId: string;
    }
  ) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId);
  }
}

export class UpdateB2BUserRoleFail extends PyStateUtils.EntityFailAction {
  readonly type = UPDATE_B2B_USER_ROLE_FAIL;
  constructor(public payload: { orgCustomerId: string; error: HttpErrorModel }) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId, payload.error);
  }
}

export class UpdateB2BUserRoleSuccess extends StateUtils.EntitySuccessAction {
  readonly type = UPDATE_B2B_USER_ROLE_SUCCESS;
  constructor(public payload: B2BUser) {
    super(B2B_USER_ENTITIES, payload.customerId, payload);
  }
}

export class UpdateB2BUserSoldTos extends StateUtils.EntityLoadAction {
  readonly type = UPDATE_B2B_USER_SOLDTOS;
  constructor(
    public payload: {
      userId: string;
      orgCustomerId: string;
      soldToIds: string[];
    }
  ) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId);
  }
}

export class UpdateB2BUserSoldTosFail extends PyStateUtils.EntityFailAction {
  readonly type = UPDATE_B2B_USER_SOLDTOS_FAIL;
  constructor(public payload: { orgCustomerId: string; error: HttpErrorModel }) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId, payload.error);
  }
}

export class UpdateB2BUserSoldTosSuccess extends StateUtils.EntitySuccessAction {
  readonly type = UPDATE_B2B_USER_SOLDTOS_SUCCESS;
  constructor(public payload: B2BUser) {
    super(B2B_USER_ENTITIES, payload.customerId, payload);
  }
}

export class UpdateB2BUserShipToAssignment extends StateUtils.EntityLoadAction {
  readonly type = UPDATE_B2B_USER_SHIP_TO_ASSIGNMENT;
  constructor(
    public payload: {
      userId: string;
      orgCustomerId: string;
      orgCustomerShipToAssignment: OrgCustomerShipToAssignment;
    }
  ) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId);
  }
}

export class UpdateB2BUserShipToAssignmentFail extends PyStateUtils.EntityFailAction {
  readonly type = UPDATE_B2B_USER_SHIP_TO_ASSIGNMENT_FAIL;
  constructor(public payload: { orgCustomerId: string; error: any }) {
    super(B2B_USER_ENTITIES, payload.orgCustomerId, payload.error);
  }
}

export class UpdateB2BUserShipToAssignmentSuccess extends StateUtils.EntitySuccessAction {
  readonly type = UPDATE_B2B_USER_SHIP_TO_ASSIGNMENT_SUCCESS;
  constructor(public payload: B2BUser) {
    super(B2B_USER_ENTITIES, payload.customerId, payload);
  }
}

export class DeleteB2BUsers extends StateUtils.LoaderLoadAction {
  readonly type = DELETE_B2B_USERS;
  constructor(
    public payload: {
      userId: string;
      orgCustomerIds: string[];
      deletedUsersStatuses: CustomerStatusEnum[];
    }
  ) {
    super(B2B_USERS_DELETE);
  }
}

export class DeleteB2BUsersFail extends PyStateUtils.LoaderFailAction {
  readonly type = DELETE_B2B_USERS_FAIL;
  constructor(public payload?: HttpErrorModel) {
    super(B2B_USERS_DELETE, payload);
  }
}

export class DeleteB2BUsersSuccess extends StateUtils.LoaderSuccessAction {
  readonly type = DELETE_B2B_USERS_SUCCESS;
  constructor(public payload?: string[]) {
    super(B2B_USERS_DELETE);
  }
}

export class SearchB2BUsers extends StateUtils.LoaderLoadAction implements PagedSearchAction {
  readonly type = SEARCH_B2B_USERS;
  readonly searchType = B2B_USER_SEARCH;
  public searchParams: B2BUserSearchParams;

  constructor(public payload: SearchB2BUsersPayload) {
    super(B2B_USER_SEARCH);
    this.searchParams = payload.searchParams;
  }
}
export class SearchB2BUsersFail extends PyStateUtils.LoaderFailAction implements PagedSearchAction {
  readonly type = SEARCH_B2B_USERS_FAIL;
  readonly searchType = B2B_USER_SEARCH;
  public searchParams: SearchParams;

  constructor(public payload: SearchB2BUsersFailPayload) {
    super(B2B_USER_SEARCH, payload.error);
    this.searchParams = payload.searchParams;
  }
}

export class ClearB2BUsersSearch implements PagedSearchAction {
  readonly type = CLEAR_B2B_USERS_SEARCH;
  readonly searchType = B2B_USER_SEARCH;
  public searchParams: B2BUserSearchParams;

  constructor(public payload: string) {
    this.searchParams = { key: payload };
  }
}

export class SearchB2BUsersSuccess extends StateUtils.LoaderSuccessAction implements PagedSearchAction {
  readonly type = SEARCH_B2B_USERS_SUCCESS;
  readonly searchType = B2B_USER_SEARCH;
  public searchParams: SearchParams;

  constructor(public payload: SearchB2BUsersSuccessPayload) {
    super(B2B_USER_SEARCH);
    this.searchParams = payload.searchParams;
  }
}

export class LoadB2BUserStatuses extends StateUtils.LoaderLoadAction {
  readonly type = LOAD_B2B_USER_STATUSES;
  constructor() {
    super(B2B_USER_STATUSES);
  }
}

export class LoadB2BUserStatusesFail extends PyStateUtils.LoaderFailAction {
  readonly type = LOAD_B2B_USER_STATUSES_FAIL;
  constructor(public payload?: any) {
    super(B2B_USER_STATUSES, payload);
  }
}

export class LoadB2BUserStatusesSuccess extends StateUtils.LoaderSuccessAction {
  readonly type = LOAD_B2B_USER_STATUSES_SUCCESS;
  constructor(public payload: CustomerStatusList) {
    super(B2B_USER_STATUSES);
  }
}

export class LoadUserRoles extends StateUtils.EntityLoadAction {
  readonly type = LOAD_USER_ROLES;
  constructor(
    public key: string,
    public userId: string,
    public configurations?: string,
    public configurationsConjunction?: boolean
  ) {
    super(USER_ROLES, key);
  }
}

export class LoadUserRolesFail extends PyStateUtils.EntityFailAction {
  readonly type = LOAD_USER_ROLES_FAIL;
  constructor(public payload: { key: string; error: HttpErrorModel }) {
    super(USER_ROLES, payload.key, payload.error);
  }
}

export class LoadUserRolesSuccess extends StateUtils.EntitySuccessAction {
  readonly type = LOAD_USER_ROLES_SUCCESS;
  constructor(public payload: { key: string; roleList: UserRoleList }) {
    super(USER_ROLES, payload.key);
  }
}

export class UpdateB2BUserStatus extends StateUtils.EntityLoadAction {
  readonly type = UPDATE_B2B_USER_STATUS;
  constructor(
    public payload: {
      userId: string;
      orgCustomerIds: string[];
      previousUsersStatuses: CustomerStatusEnum[];
      status: UpdateStatusValue;
      options?: UpdateStatusOptions;
    }
  ) {
    super(B2B_USER_ENTITIES, payload.orgCustomerIds);
  }
}

export class UpdateB2BUserStatusFail extends PyStateUtils.EntityFailAction {
  readonly type = UPDATE_B2B_USER_STATUS_FAIL;
  constructor(public payload: { orgCustomerIds: string[]; status: UpdateStatusValue; error: any }) {
    super(B2B_USER_ENTITIES, payload.orgCustomerIds, payload.error);
  }
}

export class UpdateB2BUserStatusSuccess extends StateUtils.EntitySuccessAction {
  readonly type = UPDATE_B2B_USER_STATUS_SUCCESS;
  constructor(
    public payload: {
      orgCustomerIds: string[];
      status: UpdateStatusValue;
      resultStatus: CustomerStatus;
      options?: UpdateStatusOptions;
    }
  ) {
    super(B2B_USER_ENTITIES, payload.orgCustomerIds, payload);
  }
}

export type B2BUserAction =
  | LoadB2BUser
  | LoadB2BUserFail
  | LoadB2BUserSuccess
  | CreateB2BUser
  | CreateB2BUserFail
  | CreateB2BUserSuccess
  | UpdateB2BUser
  | UpdateB2BUserFail
  | UpdateB2BUserSuccess
  | SearchB2BUsers
  | SearchB2BUsersFail
  | SearchB2BUsersSuccess
  | ClearB2BUsersSearch
  | LoadB2BUserStatuses
  | LoadB2BUserStatusesFail
  | LoadB2BUserStatusesSuccess
  | LoadUserRoles
  | LoadUserRolesFail
  | LoadUserRolesSuccess
  | UpdateB2BUserStatus
  | UpdateB2BUserStatusFail
  | UpdateB2BUserStatusSuccess
  | DeleteB2BUsers
  | DeleteB2BUsersFail
  | DeleteB2BUsersSuccess;
